import {
  createContext,
  useContext,
  type AnchorHTMLAttributes,
  type ComponentType,
  type ReactElement,
  type ReactNode,
  type Ref,
} from "react";

export type LinkProps = Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  "href"
> & {
  to: string;
  ref?: Ref<HTMLAnchorElement>;
};

interface Navigation {
  /** The app's own top-level route paths, e.g. `["/", "/rfps"]`. */
  routes: readonly string[];
  RouterLink: ComponentType<LinkProps>;
  navigate: (path: string) => void;
}

const NavigationContext = createContext<Navigation | null>(null);

export const NavigationProvider = ({
  children,
  ...navigation
}: Navigation & { children: ReactNode }): ReactElement => (
  <NavigationContext.Provider value={navigation}>
    {children}
  </NavigationContext.Provider>
);

export const useNavigation = (): Navigation => {
  const navigation = useContext(NavigationContext);
  if (!navigation) {
    throw new Error(
      "Link and useNavigate must render inside NavigationProvider",
    );
  }
  return navigation;
};

const firstSegment = (path: string): string =>
  path.replace(/^\//, "").split(/[/?#]/)[0];

/**
 * Relative paths, and paths under one of the app's routes, stay in-app.
 * Anything else (another app's path, a full URL) belongs to another app,
 * which the gateway routes to after a full page load.
 */
export const isInApp = (path: string, routes: readonly string[]): boolean => {
  const isFullUrl = /^([a-z][a-z\d+.-]*:|\/\/)/i.test(path);
  if (isFullUrl) return false;
  if (!path.startsWith("/")) return true;
  return routes.some((route) => firstSegment(route) === firstSegment(path));
};

export const useNavigate = (): ((path: string) => void) => {
  const { routes, navigate } = useNavigation();
  return (path) =>
    isInApp(path, routes) ? navigate(path) : window.location.assign(path);
};
